import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { DS_NAV, DSFolder, dsPagePath, dsStandalonePath } from './nav';

/**
 * Sidebar del Design System v3 — réplica estructural del layout de referencia
 * (Fluent 2 docs): logo arriba a la izquierda + árbol de carpetas anidadas
 * colapsables. Sin buscador (pedido explícito). Data-driven desde `DS_NAV`.
 *
 * Dividers replicados de la imagen:
 *  - Vertical: borde derecho del sidebar (separa sidebar ↔ contenido) — vive en
 *    el layout que envuelve este componente.
 *  - Horizontal: bajo el header del logo (separa header ↔ navegación).
 */

/** Fila de carpeta colapsable (nivel 1 del árbol). */
const FolderRow: React.FC<{ folder: DSFolder; open: boolean; onToggle: () => void }> = ({
  folder,
  open,
  onToggle,
}) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={open}
    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-semibold text-neutral-900 transition-colors hover:bg-neutral-100"
  >
    <ChevronRight
      className={`h-4 w-4 shrink-0 text-neutral-400 transition-transform duration-200 ${
        open ? 'rotate-90' : ''
      }`}
    />
    <span className="truncate">{folder.label}</span>
  </button>
);

/** Fila de página hoja (nivel 2 del árbol), con estado activo on-brand. */
const PageRow: React.FC<{ to: string; label: string }> = ({ to, label }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      [
        'block rounded-md py-2 pl-9 pr-3 text-sm transition-colors',
        isActive
          ? 'bg-brand-primary/[0.08] font-medium text-brand-primary'
          : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
      ].join(' ')
    }
  >
    {label}
  </NavLink>
);

/**
 * Página suelta de nivel superior (link directo, sin chevron/hijos — ej. Getting
 * Started). Se alinea con las etiquetas de carpeta (`pl-9`) y va en semibold para
 * leerse como ítem de nivel 1.
 */
const StandalonePageRow: React.FC<{ to: string; label: string }> = ({ to, label }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      [
        'block rounded-md py-2 pl-9 pr-3 text-sm font-semibold transition-colors',
        isActive
          ? 'bg-brand-primary/[0.08] text-brand-primary'
          : 'text-neutral-900 hover:bg-neutral-100',
      ].join(' ')
    }
  >
    {label}
  </NavLink>
);

export const DesignSystemSidebar: React.FC = () => {
  const { pathname } = useLocation();

  // Carpeta abierta si contiene la página activa; además la primera carpeta
  // (Foundations) arranca abierta para que las páginas se vean sin un click extra.
  const [openFolders, setOpenFolders] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    let firstFolderSeen = false;
    DS_NAV.forEach((item) => {
      if (item.kind !== 'folder') return;
      const hasActiveChild = item.pages.some(
        (page) => pathname === dsPagePath(item.slug, page.slug)
      );
      initial[item.slug] = hasActiveChild || !firstFolderSeen;
      firstFolderSeen = true;
    });
    return initial;
  });

  const toggleFolder = (slug: string) =>
    setOpenFolders((prev) => ({ ...prev, [slug]: !prev[slug] }));

  return (
    <aside className="flex h-screen w-[300px] shrink-0 flex-col border-r border-neutral-200 bg-white">
      {/* Header: logo de Atlas (mark + wordmark). Sin buscador. */}
      <div className="flex h-16 shrink-0 items-center gap-2.5 px-6">
        <img
          src="/images/estudio-atlas-logo-solo.svg"
          alt="Atlas"
          className="h-7 w-auto"
        />
        <img
          src="/images/estudio-atlas-nombre-solo.svg"
          alt="Estudio Atlas"
          className="h-[15px] w-auto"
        />
      </div>

      {/* Divider horizontal bajo el header (replica de la imagen). */}
      <div className="h-px shrink-0 bg-neutral-200" />

      {/* Árbol: páginas sueltas (nivel 1) + carpetas anidadas. */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {DS_NAV.map((item) => {
          if (item.kind === 'page') {
            return (
              <div key={item.slug} className="mb-1">
                <StandalonePageRow to={dsStandalonePath(item.slug)} label={item.label} />
              </div>
            );
          }
          const open = !!openFolders[item.slug];
          return (
            <div key={item.slug} className="mb-1">
              <FolderRow folder={item} open={open} onToggle={() => toggleFolder(item.slug)} />
              {open && item.pages.length > 0 && (
                <div className="mt-0.5">
                  {item.pages.map((page) => (
                    <PageRow
                      key={page.slug}
                      to={dsPagePath(item.slug, page.slug)}
                      label={page.label}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
};
